//src/app/features/conversations/CreateConversation/index.tsx
import { IDocument } from 'app/services/documents';
import { useCreateConversationMutation } from 'app/services/messages/createConversation';
import { IUser, useGetUserProfileByIdQuery } from 'app/services/user';
import { errorToast } from 'Components/Notifications';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ConversationLayout from '../Components/ConversationLayout';
import ConversationRecipients from '../Components/ConversationRecipients';
import ConversationTitle from '../Components/ConversationTitle';
import { EmptyMessages } from '../Components/Empty';
import MessageEditor from '../Components/MessageEditor';
import { useConversationsList } from '../Hooks/useConversationsList';

type FormInputs = {
  title: string;
  recipient_ids: number[];
  document_ids: number[];
  content: any;
};

export default function CreateConversation({
  onBack,
}: {
  onBack?: () => void;
}) {
  const [createConversation, { isLoading }] = useCreateConversationMutation();
  const { clientId } = useParams();
  const { data } = useGetUserProfileByIdQuery(+clientId!, { skip: !clientId });

  const { refreshTop } = useConversationsList();
  const [title, setTitle] = useState('');
  const [recipients, setRecipients] = useState<IUser[]>([]);

  useEffect(() => {
    if (!data) return;
    setRecipients((_) => [data.user]);
  }, [data]);
  const editorRef = useRef<{ setEditState: () => void }>(null);

  const onRecipientsChange = async (allUsers: IUser[], newUsers: IUser[]) => {
    setRecipients(allUsers);
  };

  const canSubmit = () => {
    if (title.length === 0) {
      errorToast('Please add a conversation title');
    }

    if (recipients.length === 0) {
      errorToast('Please add recipients to the conversation');
    }

    return title.length > 0 && recipients.length > 0;
  };

  const navigate = useNavigate();
  const onSubmit = (
    content: string,
    documents: IDocument[],
    raw_content: string,
  ) => {
    createConversation({
      content,
      raw_content,
      recipient_ids: recipients.map((r) => r.id),
      document_ids: documents.map((d) => d.id),
      conversation_title: title,
    })
      .unwrap()
      .then((_) => {
        refreshTop();
        navigate(`../${_.conversation_id}`);
      });
  };

  return (
    <ConversationLayout
      onBack={onBack}
      header={
        <>
          <ConversationTitle value={title} onChange={setTitle} />
          <ConversationRecipients
            isMember={true}
            value={recipients}
            locked={recipients}
            onConfirm={onRecipientsChange}
            canDeleteLocked={!clientId}
          />
        </>
      }
      content={<EmptyMessages />}
      footer={
        <MessageEditor
          canSubmit={canSubmit}
          ref={editorRef}
          onSubmit={onSubmit}
          isSubmitting={isLoading}
        />
      }
    />
  );
}
